import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { ApiError } from '../services/api';

import { useAuth } from '../contexts/AuthContext';
import { useUserData } from '../contexts/UserDataContext';
import LoginModal from '../components/LoginModal';
import AuroraBackground from '../components/AuroraBackground';
import SwipeReviewShowcase from '../components/home/SwipeReviewShowcase';
import PostingOptionsSection from '../components/home/PostingOptionsSection';
import SocialImpactSection from '../components/home/SocialImpactSection';
import PlatformAvailabilitySection from '../components/home/PlatformAvailabilitySection';
import SEO from '../components/SEO';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const HomePage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const { instagramAccountsTriggered, instagramAccountsOwning, refreshAccounts } = useUserData();
    const [accountInput, setAccountInput] = useState('');
    const [hashtagInput, setHashtagInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Login modal state for existing account auth
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [pendingAccountUrl, setPendingAccountUrl] = useState<string | null>(null);
    const [pendingHashtags, setPendingHashtags] = useState<string[] | undefined>(undefined);

    // Pricing from backend config (credit model)
    const [priceDisplay, setPriceDisplay] = useState('$29.99');
    const [priceDisplayWithCredits, setPriceDisplayWithCredits] = useState('$29.99 for 500 credits');
    const [priceAmount, setPriceAmount] = useState('29.99');
    const [creditBatchAmount, setCreditBatchAmount] = useState(500);
    useEffect(() => {
        const fetchPricing = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/config/pricing`);
                if (response.ok) {
                    const data = await response.json();
                    const credits = data.credits;
                    if (credits) {
                        setPriceDisplay(credits.batch_price_display || '$29.99');
                        setPriceDisplayWithCredits(credits.batch_price_display_with_credits || '$29.99 for 500 credits');
                        setPriceAmount((credits.batch_price_cents / 100).toFixed(2));
                        setCreditBatchAmount(credits.batch_credit_amount || 500);
                    }
                }
            } catch (err) {
                console.error('Error fetching pricing:', err);
            }
        };
        fetchPricing();
    }, []);

    // Helper to lookup hints from cached context
    const lookupHintsFromCachedContext = useCallback((accountUrl: string): { isOwner?: boolean; hasSessions?: boolean } => {
        // Extract username from URL for matching
        const extractUsername = (url: string): string => {
            try {
                const urlObj = new URL(url);
                const pathParts = urlObj.pathname.split('/').filter(p => p);
                return pathParts[0]?.toLowerCase() || '';
            } catch {
                return url.replace('@', '').toLowerCase();
            }
        };

        const targetUsername = extractUsername(accountUrl);

        // Check owning accounts first (user owns this account)
        const owningAccount = instagramAccountsOwning.find(acc => {
            const accUsername = extractUsername(acc.accountUrl);
            return accUsername === targetUsername || acc.accountId.toLowerCase() === targetUsername;
        });
        if (owningAccount) {
            console.log('[Hints] Found in owning accounts:', { isOwner: true, hasSessions: owningAccount.sessionCount > 0 });
            return { isOwner: true, hasSessions: owningAccount.sessionCount > 0 };
        }

        // Check triggered accounts (user has created sessions but may not own)
        const triggeredAccount = instagramAccountsTriggered.find(acc => {
            const accUsername = extractUsername(acc.accountUrl);
            return accUsername === targetUsername || acc.accountId.toLowerCase() === targetUsername;
        });
        if (triggeredAccount) {
            console.log('[Hints] Found in triggered accounts:', { isOwner: false, hasSessions: triggeredAccount.sessionCount > 0 });
            return { isOwner: false, hasSessions: triggeredAccount.sessionCount > 0 };
        }

        // Account not in cached context - no hints available
        console.log('[Hints] Account not found in cached context');
        return {};
    }, [instagramAccountsOwning, instagramAccountsTriggered]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!accountInput.trim()) {
            setError('Please enter your Instagram username or URL');
            return;
        }

        let accountUrl = accountInput.trim();
        if (!accountUrl.startsWith('http')) {
            accountUrl = `https://instagram.com/${accountUrl.replace('@', '')}`;
        }

        const urlPattern = /^https?:\/\/(www\.)?instagram\.com\/[a-zA-Z0-9_.]+\/?$/;
        if (!urlPattern.test(accountUrl)) {
            setError('Please enter a valid Instagram username or URL');
            return;
        }

        let hashtags: string[] | undefined;
        if (hashtagInput.trim()) {
            hashtags = hashtagInput
                .split(/[,\s]+/)
                .map(h => h.replace(/^#/, '').trim())
                .filter(h => h.length > 0);
        }

        // Require login before generating
        if (!user) {
            setPendingAccountUrl(accountUrl);
            setPendingHashtags(hashtags);
            setShowLoginModal(true);
            return;
        }

        // Pre-flight credit check
        const balance = user.creditBalance ?? 0;
        if (balance <= 0) {
            navigate('/plans');
            return;
        }

        setLoading(true);

        try {
            // Lookup hints from cached context to optimize backend Firestore reads
            const hints = lookupHintsFromCachedContext(accountUrl);
            const response = await api.createSession(accountUrl, hashtags, hints.isOwner, hints.hasSessions);
            refreshAccounts(); // fire-and-forget — update sidebar context in background
            navigate(`/session/${response.id}`);
        } catch (err: any) {
            // Check if this is an auth required error for existing account
            // Use duck typing to handle module boundary issues
            const isAuthRequired =
                (err instanceof ApiError && err.code === 'auth_required') ||
                (err?.code === 'auth_required') ||
                (err?.status === 401) ||
                (err?.message?.includes('Authentication required'));

            const isNoCredits =
                (err instanceof ApiError && err.code === 'subscription_required') ||
                (err?.code === 'subscription_required') ||
                (err?.status === 402);

            console.log('Session creation error:', err, 'isAuthRequired:', isAuthRequired, 'isNoCredits:', isNoCredits);

            if (isAuthRequired) {
                // Store pending session info and show login modal
                setPendingAccountUrl(accountUrl);
                setPendingHashtags(hashtags);
                setShowLoginModal(true);
                setError(null);
            } else if (isNoCredits) {
                navigate(`/plans?redirect=${encodeURIComponent(location.pathname)}`);
            } else {
                setError(err instanceof Error ? err.message : 'Failed to create session');
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle successful login - retry session creation
    const handleLoginSuccess = async () => {
        setShowLoginModal(false);
        if (pendingAccountUrl) {
            setLoading(true);
            setError(null);
            try {
                // After login, user context may have changed - lookup hints again
                const hints = lookupHintsFromCachedContext(pendingAccountUrl);
                const response = await api.createSession(pendingAccountUrl, pendingHashtags, hints.isOwner, hints.hasSessions);
                refreshAccounts(); // fire-and-forget — update sidebar context in background
                navigate(`/session/${response.id}`);
            } catch (err: any) {
                const isNoCredits =
                    (err instanceof ApiError && err.code === 'subscription_required') ||
                    (err?.code === 'subscription_required') ||
                    (err?.status === 402);
                if (isNoCredits) {
                    navigate('/plans');
                } else {
                    setError(err instanceof Error ? err.message : 'Failed to create session');
                }
            } finally {
                setLoading(false);
                setPendingAccountUrl(null);
                setPendingHashtags(undefined);
            }
        }
    };

    // Extract username from URL for display
    const extractUsernameFromUrl = (url: string) => {
        try {
            const urlObj = new URL(url);
            const pathParts = urlObj.pathname.split('/').filter(p => p);
            return pathParts[0] || url;
        } catch {
            return url.replace(/^@/, '');
        }
    };

    const stats = [
        { value: '10K+', label: 'Comments Posted' },
        { value: '500+', label: 'Active Users' },
        { value: '98%', label: 'Satisfaction Rate' },
        { value: '50+', label: 'Countries' },
    ];

    const features = [
        {
            title: 'AI Comment Generation',
            description: 'Our AI analyzes posts and crafts personalized, genuine comments that resonate with your target audience.',
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
            ),
        },
        {
            title: 'Smart Targeting',
            description: 'Identify and engage with users who are most likely to become valuable connections in your niche.',
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="12" r="10" />
                    <circle cx="12" cy="12" r="6" />
                    <circle cx="12" cy="12" r="2" />
                </svg>
            ),
        },
        {
            title: 'Safe Automation',
            description: 'Built-in rate limiting and human-like behavior patterns keep your account safe and secure.',
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
            ),
        },
        {
            title: 'Analytics Dashboard',
            description: 'Track your engagement growth, monitor comment performance, and optimize your strategy.',
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M18 20V10M12 20V4M6 20v-6" />
                </svg>
            ),
        },
    ];

    const steps = [
        {
            number: '01',
            title: 'Enter Your Account',
            description: 'Simply type in your Instagram username or paste your profile URL to get started.',
        },
        {
            number: '02',
            title: 'AI Analyzes',
            description: 'Our AI scans your network and engagement patterns to identify valuable connections.',
        },
        {
            number: '03',
            title: 'Review Comments',
            description: 'Review each AI-generated comment with simple Accept/Decline buttons.',
        },
        {
            number: '04',
            title: 'Post & Grow',
            description: 'Let us automatically post your approved comments at safe intervals.',
        },
    ];

    return (
        <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif' }}>
            <SEO
                title="Vibe Commenting | AI-Powered Instagram Growth & Engagement Tool"
                description="Grow your Instagram authentically with AI-generated comments. Vibe Commenting creates thoughtful, personalized comments you approve before posting. Free to start."
                path="/"
                jsonLd={[
                    {
                        "@context": "https://schema.org",
                        "@type": "Organization",
                        "name": "Vibe Commenting",
                        "url": "https://vibecommenting.com",
                        "logo": "https://vibecommenting.com/logo-white-512.png",
                        "description": "AI-powered Instagram commenting tool for authentic engagement and growth"
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "SoftwareApplication",
                        "name": "Vibe Commenting",
                        "applicationCategory": "SocialNetworkingApplication",
                        "operatingSystem": "iOS, Web",
                        "offers": {
                            "@type": "AggregateOffer",
                            "lowPrice": "0",
                            "highPrice": priceAmount,
                            "priceCurrency": "USD",
                            "offerCount": "2"
                        },
                        "description": "AI-powered Instagram comment generator that creates authentic, personalized comments you approve before posting."
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": [
                            {
                                "@type": "Question",
                                "name": "How does Vibe Commenting work?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Vibe Commenting uses AI to generate personalized, thoughtful comments for Instagram posts in your niche. You review each comment with a swipe interface and approve before anything is posted."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Is Vibe Commenting free?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": `Yes, new users get 10 free credits to try Vibe Commenting. Purchase ${creditBatchAmount} credits for ${priceDisplay} to unlock full commenting power.`
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Will comments look spammy?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "No. Every comment is AI-generated to be contextual and thoughtful based on the actual post content. Plus, you review and approve every comment before it's posted."
                                }
                            }
                        ]
                    }
                ]}
            />
            {/* Aurora Background Animation */}
            <AuroraBackground />

            {/* Hero Section */}
            <section style={{
                minHeight: '100vh',
                background: 'transparent',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '120px 24px 80px',
                position: 'relative',
                overflow: 'hidden',
            }}>

                <div style={{
                    maxWidth: '900px',
                    textAlign: 'center',
                    position: 'relative',
                    zIndex: 1,
                }}>
                    {/* Main Headline */}
                    <h1 style={{
                        fontSize: 'clamp(32px, 6vw, 64px)',
                        fontWeight: 500,
                        color: '#000000',
                        lineHeight: 1.1,
                        marginBottom: '32px',
                        letterSpacing: '-0.03em',
                    }}>
                        Replace your like button with auto generated comments on Instagram
                    </h1>

                    {/* Subheadline */}
                    <div style={{
                        fontSize: 'clamp(16px, 2vw, 20px)',
                        color: '#333333',
                        marginBottom: '48px',
                        lineHeight: 1.6,
                        maxWidth: '560px',
                        margin: '0 auto 48px',
                        fontWeight: 400,
                    }}>
                        <p style={{ marginBottom: '16px' }}>
                            People don't read DMs, but they read comments on their own posts.
                            Easily expand connection by commenting on other's post:
                        </p>
                        <ol style={{
                            textAlign: 'left',
                            paddingLeft: '24px',
                            margin: 0,
                        }}>
                            <li style={{ marginBottom: '4px' }}>Enter your Instagram account ID</li>
                            <li>Swipe to accept or decline the generated comments</li>
                        </ol>
                    </div>

                    {/* Input Form - Immediately visible */}
                    <form onSubmit={handleSubmit} style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        maxWidth: '520px',
                        margin: '0 auto 32px',
                    }}>
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            flexWrap: 'wrap',
                        }}>
                            <input
                                type="text"
                                value={accountInput}
                                onChange={(e) => setAccountInput(e.target.value)}
                                placeholder="Enter your Instagram username"
                                disabled={loading}
                                autoFocus
                                className="hero-input"
                                style={{
                                    flex: '1 1 280px',
                                    padding: '18px 20px',
                                    background: '#FFFFFF',
                                    border: '1px solid #E5E5E5',
                                    borderRadius: '4px',
                                    color: '#000000',
                                    fontSize: '16px',
                                    outline: 'none',
                                    transition: 'all 0.2s ease',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                                }}
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    padding: '18px 32px',
                                    background: loading ? '#A78BFA' : '#7C3AED',
                                    color: '#FFFFFF',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s ease',
                                    whiteSpace: 'nowrap',
                                    boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
                                }}
                            >
                                {loading ? 'Starting...' : 'Start with 10 free comments'}
                            </button>
                        </div>

                        <input
                            type="text"
                            value={hashtagInput}
                            onChange={(e) => setHashtagInput(e.target.value)}
                            placeholder="Optional: hashtags to explore (e.g. fitness, yoga)"
                            disabled={loading}
                            className="hero-input"
                            style={{
                                width: '100%',
                                padding: '14px 20px',
                                background: '#FFFFFF',
                                border: '1px solid #E5E5E5',
                                borderRadius: '4px',
                                color: '#000000',
                                fontSize: '14px',
                                outline: 'none',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                            }}
                        />

                        {error && (
                            <div style={{
                                color: '#EF4444',
                                fontSize: '14px',
                                padding: '12px 20px',
                                background: 'rgba(239, 68, 68, 0.08)',
                                borderRadius: '4px',
                                border: '1px solid rgba(239, 68, 68, 0.2)',
                            }}>
                                {error}
                            </div>
                        )}

                        <p style={{
                            color: '#666666',
                            fontSize: '13px',
                            margin: '4px 0 0 0',
                        }}>
                            No credit card required. Sign up and get 10 free credits.
                        </p>
                    </form>
                </div>

                {/* Scroll indicator */}
                <div style={{
                    position: 'absolute',
                    bottom: '40px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    color: '#999999',
                }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ animation: 'bounce 2s infinite' }}>
                        <path d="M7 13l5 5 5-5M7 6l5 5 5-5" />
                    </svg>
                </div>
            </section>

            {/* Stats Section */}
            <section style={{
                background: '#FFFFFF',
                borderBottom: '1px solid #E5E5E5',
            }}>
                <div style={{
                    maxWidth: '1200px',
                    margin: '0 auto',
                    padding: '80px 24px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '48px',
                    textAlign: 'center',
                }}>
                    {stats.map((stat, index) => (
                        <div key={index}>
                            <div style={{
                                fontSize: 'clamp(36px, 5vw, 56px)',
                                fontWeight: 500,
                                color: '#000000',
                                letterSpacing: '-0.02em',
                                marginBottom: '8px',
                            }}>
                                {stat.value}
                            </div>
                            <div style={{
                                fontSize: '14px',
                                color: '#333333',
                                fontWeight: 500,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                            }}>
                                {stat.label}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Swipe Review Showcase */}
            <SwipeReviewShowcase />

            {/* Features Section */}
            <section id="features" style={{
                background: '#F7F7F7',
                padding: '120px 24px',
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '80px' }}>
                        <h2 style={{
                            fontSize: 'clamp(32px, 5vw, 48px)',
                            fontWeight: 500,
                            color: '#000000',
                            marginBottom: '16px',
                            letterSpacing: '-0.02em',
                        }}>
                            Powerful features for<br />authentic engagement
                        </h2>
                        <p style={{
                            fontSize: '18px',
                            color: '#333333',
                            maxWidth: '500px',
                            margin: '0 auto',
                        }}>
                            Everything you need to grow your Instagram presence naturally
                        </p>
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: '24px',
                    }}>
                        {features.map((feature, index) => (
                            <div
                                key={index}
                                style={{
                                    background: '#FFFFFF',
                                    padding: '48px 32px',
                                    borderRadius: '8px',
                                    border: '1px solid #E5E5E5',
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.08)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <div style={{
                                    width: '64px',
                                    height: '64px',
                                    borderRadius: '8px',
                                    background: '#7C3AED',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '24px',
                                    color: '#FFFFFF',
                                }}>
                                    {feature.icon}
                                </div>
                                <h3 style={{
                                    fontSize: '20px',
                                    fontWeight: 600,
                                    color: '#000000',
                                    marginBottom: '12px',
                                }}>
                                    {feature.title}
                                </h3>
                                <p style={{
                                    fontSize: '15px',
                                    color: '#444444',
                                    lineHeight: 1.6,
                                }}>
                                    {feature.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Two Posting Options */}
            <PostingOptionsSection />

            {/* How It Works Section */}
            <section id="how-it-works" style={{
                background: '#FFFFFF',
                padding: '120px 24px',
            }}>
                <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '80px' }}>
                        <h2 style={{
                            fontSize: 'clamp(32px, 5vw, 48px)',
                            fontWeight: 500,
                            color: '#000000',
                            marginBottom: '16px',
                            letterSpacing: '-0.02em',
                        }}>
                            How it works
                        </h2>
                        <p style={{
                            fontSize: '18px',
                            color: '#333333',
                        }}>
                            Get started in four simple steps
                        </p>
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: '48px',
                    }}>
                        {steps.map((step, index) => (
                            <div key={index} style={{ textAlign: 'center' }}>
                                <div style={{
                                    fontSize: '64px',
                                    fontWeight: 300,
                                    color: '#E5E5E5',
                                    marginBottom: '16px',
                                    lineHeight: 1,
                                }}>
                                    {step.number}
                                </div>
                                <h3 style={{
                                    fontSize: '18px',
                                    fontWeight: 600,
                                    color: '#000000',
                                    marginBottom: '12px',
                                }}>
                                    {step.title}
                                </h3>
                                <p style={{
                                    fontSize: '15px',
                                    color: '#444444',
                                    lineHeight: 1.6,
                                }}>
                                    {step.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Social Impact */}
            <SocialImpactSection />

            {/* Platform Availability */}
            <PlatformAvailabilitySection />

            {/* CTA Section */}
            <section id="get-started" style={{
                background: '#000000',
                padding: '100px 24px',
            }}>
                <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
                    <h2 style={{
                        fontSize: 'clamp(32px, 5vw, 48px)',
                        fontWeight: 500,
                        color: '#FFFFFF',
                        marginBottom: '16px',
                        letterSpacing: '-0.02em',
                    }}>
                        Ready to grow?
                    </h2>
                    <p style={{
                        fontSize: '18px',
                        color: 'rgba(255, 255, 255, 0.6)',
                        marginBottom: '32px',
                    }}>
                        Start building authentic connections today
                    </p>
                    <button
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        style={{
                            padding: '18px 48px',
                            background: '#7C3AED',
                            color: '#FFFFFF',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '16px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                        }}
                    >
                        Get Started Free
                    </button>
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" style={{
                background: '#FFFFFF',
                padding: '120px 24px',
            }}>
                <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '64px' }}>
                        <h2 style={{
                            fontSize: 'clamp(32px, 5vw, 48px)',
                            fontWeight: 500,
                            color: '#000000',
                            marginBottom: '16px',
                            letterSpacing: '-0.02em',
                        }}>
                            Simple, transparent pricing
                        </h2>
                        <p style={{
                            fontSize: '18px',
                            color: '#333333',
                        }}>
                            Start free, upgrade when you're ready
                        </p>
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: '24px',
                        maxWidth: '800px',
                        margin: '0 auto',
                    }}>
                        {/* Free Plan */}
                        <div style={{
                            background: '#F7F7F7',
                            padding: '48px 32px',
                            borderRadius: '8px',
                            border: '1px solid #E5E5E5',
                        }}>
                            <div style={{
                                fontSize: '14px',
                                fontWeight: 600,
                                color: '#666666',
                                marginBottom: '16px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                            }}>
                                Free
                            </div>
                            <div style={{
                                fontSize: '48px',
                                fontWeight: 500,
                                color: '#000000',
                                marginBottom: '24px',
                            }}>
                                $0
                            </div>
                            <ul style={{
                                listStyle: 'none',
                                padding: 0,
                                margin: '0 0 32px 0',
                            }}>
                                {['10 free credits on sign-up', 'AI-powered generation', 'Basic targeting', 'Email support'].map((item, i) => (
                                    <li key={i} style={{
                                        padding: '12px 0',
                                        borderBottom: '1px solid #E5E5E5',
                                        color: '#333333',
                                        fontSize: '15px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                    }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="2">
                                            <polyline points="20,6 9,17 4,12" />
                                        </svg>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <a
                                href="#get-started"
                                style={{
                                    display: 'block',
                                    textAlign: 'center',
                                    padding: '14px 24px',
                                    background: '#FFFFFF',
                                    color: '#000000',
                                    borderRadius: '4px',
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    border: '1px solid #E5E5E5',
                                }}
                            >
                                Get Started
                            </a>
                        </div>

                        {/* Pro Plan */}
                        <div style={{
                            background: '#000000',
                            padding: '48px 32px',
                            borderRadius: '8px',
                            position: 'relative',
                        }}>
                            <div style={{
                                position: 'absolute',
                                top: '-12px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                background: '#FFFFFF',
                                color: '#000000',
                                padding: '6px 16px',
                                borderRadius: '100px',
                                fontSize: '12px',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                            }}>
                                Popular
                            </div>
                            <div style={{
                                fontSize: '14px',
                                fontWeight: 600,
                                color: 'rgba(255, 255, 255, 0.6)',
                                marginBottom: '16px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                            }}>
                                Credit Pack
                            </div>
                            <div style={{
                                fontSize: '48px',
                                fontWeight: 500,
                                color: '#FFFFFF',
                                marginBottom: '24px',
                            }}>
                                {priceDisplay}<span style={{ fontSize: '18px', fontWeight: 400 }}> one-time</span>
                            </div>
                            <ul style={{
                                listStyle: 'none',
                                padding: 0,
                                margin: '0 0 32px 0',
                            }}>
                                {['Everything in Free, plus:', `${creditBatchAmount} AI-generated comments`, 'Credits never expire', 'Buy more anytime'].map((item, i) => (
                                    <li key={i} style={{
                                        padding: '12px 0',
                                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                                        color: '#FFFFFF',
                                        fontSize: '15px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                    }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2">
                                            <polyline points="20,6 9,17 4,12" />
                                        </svg>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <a
                                href="#get-started"
                                style={{
                                    display: 'block',
                                    textAlign: 'center',
                                    padding: '14px 24px',
                                    background: '#FFFFFF',
                                    color: '#000000',
                                    borderRadius: '4px',
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                }}
                            >
                                Start Free Trial
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer style={{
                background: '#000000',
                padding: '80px 24px 40px',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                        gap: '48px',
                        marginBottom: '64px',
                    }}>
                        {/* Brand */}
                        <div>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                marginBottom: '24px',
                            }}>
                                <img
                                    src="/logo-black-192.png"
                                    alt="Vibe Commenting"
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '6px',
                                        boxShadow: '0 2px 8px rgba(255, 255, 255, 0.2)',
                                    }}
                                />
                                <span style={{
                                    fontSize: '18px',
                                    fontWeight: 600,
                                    color: '#FFFFFF',
                                }}>
                                    vibecommenting
                                </span>
                            </div>
                            <p style={{
                                fontSize: '14px',
                                color: 'rgba(255, 255, 255, 0.5)',
                                lineHeight: 1.6,
                            }}>
                                Building authentic Instagram connections with AI.
                            </p>
                        </div>

                        {/* Product */}
                        <div>
                            <h4 style={{
                                fontSize: '13px',
                                fontWeight: 600,
                                color: '#FFFFFF',
                                marginBottom: '20px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                            }}>
                                Product
                            </h4>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {[
                                    { label: 'Features', href: '#features' },
                                    { label: 'Pricing', href: '#pricing' },
                                ].map((item, i) => (
                                    <li key={i} style={{ marginBottom: '12px' }}>
                                        <a href={item.href} style={{
                                            color: 'rgba(255, 255, 255, 0.5)',
                                            textDecoration: 'none',
                                            fontSize: '14px',
                                            transition: 'color 0.2s',
                                        }}>
                                            {item.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Company */}
                        <div>
                            <h4 style={{
                                fontSize: '13px',
                                fontWeight: 600,
                                color: '#FFFFFF',
                                marginBottom: '20px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                            }}>
                                Company
                            </h4>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                <li style={{ marginBottom: '12px' }}>
                                    <a href="/privacy" style={{
                                        color: 'rgba(255, 255, 255, 0.5)',
                                        textDecoration: 'none',
                                        fontSize: '14px',
                                        transition: 'color 0.2s',
                                    }}>
                                        Privacy
                                    </a>
                                </li>
                                <li style={{ marginBottom: '12px' }}>
                                    <a href="/contact" style={{
                                        color: 'rgba(255, 255, 255, 0.5)',
                                        textDecoration: 'none',
                                        fontSize: '14px',
                                        transition: 'color 0.2s',
                                    }}>
                                        Contact
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Bottom */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingTop: '32px',
                        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                        flexWrap: 'wrap',
                        gap: '16px',
                    }}>
                        <p style={{
                            color: 'rgba(255, 255, 255, 0.4)',
                            fontSize: '13px',
                        }}>
                            © 2026 Atelier Persopol OÜ. All rights reserved.
                        </p>
                        <div style={{ display: 'flex', gap: '24px' }}>
                            {['Twitter', 'Instagram', 'LinkedIn'].map((social, i) => (
                                <span key={i} role="link" tabIndex={0} style={{
                                    color: 'rgba(255, 255, 255, 0.4)',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    transition: 'color 0.2s',
                                }}>
                                    {social}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </footer>

            {/* Global Styles */}
            <style>
                {`
                    @keyframes bounce {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(8px); }
                    }
                    .hero-input::placeholder {
                        color: #999999;
                    }
                    .hero-input:focus {
                        border-color: #7C3AED;
                        box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1), 0 2px 8px rgba(0, 0, 0, 0.04);
                    }
                    * {
                        box-sizing: border-box;
                    }
                    html {
                        scroll-behavior: smooth;
                    }
                `}
            </style>

            {/* Login Modal for existing account authentication */}
            <LoginModal
                isOpen={showLoginModal}
                onClose={() => {
                    setShowLoginModal(false);
                    setPendingAccountUrl(null);
                    setPendingHashtags(undefined);
                }}
                onSuccess={handleLoginSuccess}
                title="Sign In Required"
                message={`There is already a session existing for the Instagram account @${pendingAccountUrl ? extractUsernameFromUrl(pendingAccountUrl) : ''}. Please sign in to continue.`}
            />
        </div>
    );
};

export default HomePage;
